/**
 * logos.ts — Categoria "Logos" (10 modelos premium)
 *
 * Faixas de clientes e parceiros montadas com seção em camadas + `ui.logos`.
 * As marcas são trocadas pelo usuário no painel de propriedades.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote A)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para a faixa de logos. */
function faixa(nota: string, cinza = true, alinhamento = 'center') {
  return b('ui.logos', { items: '', note: nota, grayscale: cinza, align: alinhamento, motion: 'fade-up' });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'logos.faixa-clara',
    category: 'logos',
    label: 'Faixa clara de marcas',
    description: 'Marcas em tons de cinza sobre fundo claro.',
    preview: 'logos',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '1120px', motion: 'fade' }, [faixa('Empresas que confiam')]),
    ],
  },

  {
    key: 'logos.faixa-escura',
    category: 'logos',
    label: 'Faixa escura de marcas',
    description: 'Fundo escuro com brilho e marcas em destaque.',
    preview: 'logos',
    outputs: WEB,
    order: 2,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'md', align: 'center', maxWidth: '1120px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', motion: 'fade' },
        [faixa('Parceiros e clientes', false)],
      ),
    ],
  },

  {
    key: 'logos.com-titulo',
    category: 'logos',
    label: 'Marcas com título',
    description: 'Título de seção acima da faixa de marcas.',
    preview: 'logos',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', backgroundColor: '#f8fafc', motion: 'fade-up' }, [
        b('ui.title', { text: 'Quem já trabalha com a gente', size: 'md', level: 'h2', align: 'center' }),
        faixa(''),
      ]),
    ],
  },

  {
    key: 'logos.com-numero',
    category: 'logos',
    label: 'Marcas com número',
    description: 'Um resultado em destaque ao lado das marcas.',
    preview: 'logos',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade' }, [
        b('ui.stat', { value: '+2.000', label: 'empresas atendidas', align: 'center' }),
        faixa('Em todos os portes e segmentos'),
      ]),
    ],
  },

  {
    key: 'logos.ao-lado-texto',
    category: 'logos',
    label: 'Marcas ao lado do texto',
    description: 'Texto explicativo à esquerda e marcas à direita.',
    preview: 'split-right',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Confiança construída no dia a dia', size: 'md', level: 'h2' }),
        b('ui.paragraph', { text: 'Atendemos empresas de diferentes setores com o mesmo cuidado.' }),
        faixa('', true, 'left'),
      ]),
    ],
  },

  {
    key: 'logos.com-selo',
    category: 'logos',
    label: 'Marcas com selo',
    description: 'Selo de credibilidade acima da faixa de marcas.',
    preview: 'logos',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '1000px', motion: 'fade' }, [
        b('ui.badge', { text: 'Prova social', variant: 'outline', align: 'center' }),
        faixa('Marcas que já passaram por aqui'),
      ]),
    ],
  },

  {
    key: 'logos.sobre-foto',
    category: 'logos',
    label: 'Marcas sobre foto',
    description: 'Foto de ambiente escurecida com as marcas por cima.',
    preview: 'logos',
    outputs: WEB,
    order: 7,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.cidadeNoite, overlay: 'darker', paddingY: 'lg', align: 'center', textColor: '#ffffff', maxWidth: '1120px', motion: 'fade' },
        [faixa('Presentes em todo o país', false)],
      ),
    ],
  },

  {
    key: 'logos.cartao',
    category: 'logos',
    label: 'Marcas em cartão',
    description: 'Faixa dentro de um cartão arredondado com sombra.',
    preview: 'logos',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', backgroundColor: '#ffffff', radius: 'xl', motion: 'fade-up' }, [
        faixa('Clientes ativos'),
      ]),
    ],
  },

  {
    key: 'logos.com-cta',
    category: 'logos',
    label: 'Marcas com convite',
    description: 'Faixa de marcas seguida de um botão de contato.',
    preview: 'cta',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', backgroundGradient: 'soft', motion: 'fade' }, [
        faixa('Faça parte dessa lista'),
        b('ui.buttons', { items: 'Quero uma proposta|#formulario|solid', align: 'center' }),
      ]),
    ],
  },

  {
    key: 'logos.discreta',
    category: 'logos',
    label: 'Faixa discreta',
    description: 'Versão compacta, boa para logo abaixo da capa.',
    preview: 'logos',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'sm', align: 'center', maxWidth: '960px', motion: 'fade' }, [faixa('')]),
    ],
  },
];

registerPatterns(modelos);
